import React, { useState } from "react";
import { Icon } from "@iconify/react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  getBulkPaymentUploadTemplate,
  previewBulkPaymentUpload,
  uploadBulkPaymentDetails,
} from "../../../api/TransactionApisV2";
import { FileDropzone } from "../../../components/shared/FileDropzone";
import { Instructions } from "../../../components/shared/Instructions";
import {
  HStack,
  PrimaryCTAButton,
  SecondaryCTAButton,
  TertiaryCTAButton,
  VStack,
} from "../../../components/utils";
import {
  BulkPaymentUploadError,
  BulkPaymentUploadPreview,
} from "../../../types/BulkPaymentImport";
import { downloadBlobObject } from "../../../utils/DownloadFile";

const formatAmount = (amount: number | null) =>
  amount == null ? "" : `₹${amount.toLocaleString("en-IN")}`;

type BulkPaymentUploadStep = "UPLOAD" | "PREVIEW";

interface BulkPaymentUploadDialogProps {
  eventId: string;
  eventName: string;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

export default function BulkPaymentUploadDialog({
  eventId,
  eventName,
  onClose,
  onSuccess,
}: BulkPaymentUploadDialogProps) {
  const [step, setStep] = useState<BulkPaymentUploadStep>("UPLOAD");
  const [file, setFile] = useState<File>();
  const [preview, setPreview] = useState<BulkPaymentUploadPreview>();
  const [errors, setErrors] = useState<BulkPaymentUploadError[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const busy = isPreviewing || isUploading;

  const requestClose = () => {
    if (busy) return;
    if (file) setConfirmDiscard(true);
    else onClose();
  };

  const downloadTemplate = async () => {
    setIsDownloading(true);
    try {
      const workbook = await getBulkPaymentUploadTemplate(eventId);
      downloadBlobObject(workbook, "Exercise Payment Details.xlsx");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.errorRaw ||
          "Could not download the payment template."
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const extractErrors = (error: any): BulkPaymentUploadError[] =>
    Array.isArray(error?.response?.data?.errors)
      ? error.response.data.errors
      : [
          {
            row: 0,
            message:
              error?.response?.data?.errorRaw ||
              "Could not process this workbook. Try again.",
          },
        ];

  const previewFile = async () => {
    if (!file) return;
    setIsPreviewing(true);
    setErrors([]);
    try {
      const response = await previewBulkPaymentUpload(eventId, file);
      setPreview(response.data);
      setErrors(response.data.errors);
      setStep("PREVIEW");
    } catch (error) {
      setPreview(undefined);
      setErrors(extractErrors(error));
      setStep("PREVIEW");
    } finally {
      setIsPreviewing(false);
    }
  };

  const uploadFile = async () => {
    if (!file || !preview || preview.errors.length) return;
    setIsUploading(true);
    let uploaded = false;
    try {
      const response = await uploadBulkPaymentDetails(eventId, file);
      toast.success(
        `${response.data.createdCount} payment details uploaded for approval.`
      );
      uploaded = true;
    } catch (error) {
      const uploadErrors = extractErrors(error);
      setErrors(uploadErrors);
      setPreview(undefined);
    } finally {
      setIsUploading(false);
    }
    if (uploaded) {
      onClose();
      try {
        await onSuccess();
      } catch {
        toast.error("Payments uploaded, but the event data could not refresh.");
      }
    }
  };

  return (
    <>
      <Dialog
        open
        onClose={requestClose}
        aria-labelledby="bulk-payment-upload-title"
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: "calc(100vh - 32px)" } }}
      >
        <VStack className="w-full max-h-[calc(100vh-32px)] gap-0 bg-white">
          <HStack className="items-start justify-between gap-4 border-b border-borderColor p-6">
            <VStack className="min-w-0 gap-1">
              <h2
                id="bulk-payment-upload-title"
                className="text-base leading-6 font-semibold text-headerColor"
              >
                Bulk upload payment details
              </h2>
              <p className="text-sm leading-5 text-secondaryText">
                {step === "PREVIEW"
                  ? "Review the payment details before uploading them for approval."
                  : `Download the template and upload exercise and tax payments for ${
                      eventName || "this exercise event"
                    }.`}
              </p>
            </VStack>
            <button
              type="button"
              aria-label="Close bulk payment upload dialog"
              onClick={requestClose}
              className="cursor-pointer text-lg font-medium disabled:cursor-not-allowed disabled:opacity-50"
              disabled={busy}
            >
              X
            </button>
          </HStack>
          <VStack className="min-h-0 flex-1 gap-6 overflow-y-auto px-6 py-5">
            {step === "UPLOAD" && (
              <VStack className="mx-auto w-full max-w-2xl gap-6 py-2">
                <VStack className="gap-3">
                  <VStack className="gap-1">
                    <h4 className="text-base font-medium text-primaryText">
                      Download the Excel template
                    </h4>
                    <div className="text-xs2 leading-5 text-secondaryText">
                      <p>The downloaded file contains two sheets:</p>
                      <ul className="list-disc pl-5">
                        <li>
                          <strong>Payment details:</strong> Enter one payment
                          tranche per row.
                        </li>
                        <li>
                          <strong>Reference sheet:</strong> Lists eligible
                          requests, remaining balances, and available
                          financiers.
                        </li>
                      </ul>
                    </div>
                  </VStack>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-3 rounded-lg border-2 border-inputBorder p-4 text-left hover:bg-slate-light disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={downloadTemplate}
                    disabled={isDownloading || busy}
                  >
                    <HStack className="min-w-0 items-center gap-3">
                      <Icon
                        icon="vscode-icons:file-type-excel"
                        height={30}
                        aria-hidden="true"
                      />
                      <span className="text-base font-medium text-primaryText">
                        Exercise_Payment_Details.xlsx
                      </span>
                    </HStack>
                    <Icon
                      icon={
                        isDownloading
                          ? "svg-spinners:ring-resize"
                          : "material-symbols:download-sharp"
                      }
                      height={24}
                      aria-hidden="true"
                    />
                  </button>
                </VStack>
                <FileDropzone
                  inputId="bulk-payment-workbook"
                  selectedFile={file}
                  acceptedFileExtensions={[".xlsx"]}
                  instructionText="Excel workbook only (.xlsx), up to 10 MB"
                  maxFileSize={10 * 1024 * 1024}
                  disabled={busy}
                  onFileSelect={(selected) => {
                    setFile(selected);
                    setPreview(undefined);
                    setErrors([]);
                  }}
                  onClear={() => {
                    setFile(undefined);
                    setPreview(undefined);
                    setErrors([]);
                  }}
                />
                <Instructions
                  data={[
                    "Enter one payment tranche per row. Exercise and tax payments can share a workbook.",
                    "Self-funded rows need amount, date, reference ID, and payment mode.",
                    "If you select ESOP Financing, Payment Date and Reference ID are not required. Leave Payment Date, Reference ID, and Mode of Payment blank. Use one row for the full amount and a financier ID from the Reference sheet.",
                    "Bulk uploads do not include receipts. Uploaded payments will wait for approval.",
                  ]}
                />
                {errors.length > 0 && (
                  <section
                    aria-labelledby="bulk-payment-errors"
                    aria-live="polite"
                    className="rounded-lg border border-red-200 bg-red-50 p-4"
                  >
                    <VStack className="gap-2">
                      <h3
                        id="bulk-payment-errors"
                        className="text-base leading-6 font-semibold text-red-800"
                      >
                        {errors.length} workbook{" "}
                        {errors.length === 1 ? "error" : "errors"}
                      </h3>
                      <ul className="list-disc pl-5 text-sm leading-5 text-red-800">
                        {errors.map((error, index) => (
                          <li
                            key={`${error.row}-${
                              error.field || "file"
                            }-${index}`}
                          >
                            {error.row
                              ? `Row ${error.row}${
                                  error.field ? `, ${error.field}` : ""
                                }: `
                              : ""}
                            {error.message}
                          </li>
                        ))}
                      </ul>
                    </VStack>
                  </section>
                )}
              </VStack>
            )}

            {step === "PREVIEW" && (
              <VStack className="gap-6">
                {errors.length > 0 && (
                  <section
                    aria-labelledby="bulk-payment-errors"
                    aria-live="polite"
                    className="rounded-lg border border-red-200 bg-red-50 p-4"
                  >
                    <VStack className="gap-2">
                      <h3
                        id="bulk-payment-errors"
                        className="text-base leading-6 font-semibold text-red-800"
                      >
                        {errors.length} workbook{" "}
                        {errors.length === 1 ? "error" : "errors"}
                      </h3>
                      <ul className="list-disc pl-5 text-sm leading-5 text-red-800">
                        {errors.map((error, index) => (
                          <li
                            key={`${error.row}-${
                              error.field || "file"
                            }-${index}`}
                          >
                            {error.row
                              ? `Row ${error.row}${
                                  error.field ? `, ${error.field}` : ""
                                }: `
                              : ""}
                            {error.message}
                          </li>
                        ))}
                      </ul>
                    </VStack>
                  </section>
                )}
                {preview && (
                  <section aria-labelledby="bulk-payment-preview-title">
                    <VStack className="gap-4">
                      <HStack className="items-start justify-between gap-4">
                        <VStack className="gap-1">
                          <h3
                            id="bulk-payment-preview-title"
                            className="text-base leading-6 font-semibold text-headerColor"
                          >
                            Review payments
                          </h3>
                          <p className="text-sm text-secondaryText">
                            {preview.totals.rows} rows. Exercise:{" "}
                            {formatAmount(preview.totals.exerciseAmount)}. Tax:{" "}
                            {formatAmount(preview.totals.taxAmount)}.
                          </p>
                        </VStack>
                        <span className="shrink-0 text-xs2 font-medium text-secondaryText">
                          Read-only preview
                        </span>
                      </HStack>
                      <div className="w-full overflow-x-auto border border-borderColor rounded-lg">
                        <table className="w-full min-w-max text-xs2 leading-5">
                          <thead className="bg-slate-light text-secondaryText">
                            <tr>
                              <th scope="col" className="p-3 text-left">
                                Row
                              </th>
                              <th scope="col" className="p-3 text-left">
                                Employee
                              </th>
                              <th scope="col" className="p-3 text-left">
                                Request ID
                              </th>
                              <th scope="col" className="p-3 text-left">
                                Payment type
                              </th>
                              <th scope="col" className="p-3 text-left">
                                Financing
                              </th>
                              <th scope="col" className="p-3 text-right">
                                Amount
                              </th>
                              <th scope="col" className="p-3 text-left">
                                Date
                              </th>
                              <th scope="col" className="p-3 text-left">
                                Reference ID
                              </th>
                              <th scope="col" className="p-3 text-left">
                                Mode or financier
                              </th>
                              <th scope="col" className="p-3 text-left">
                                Remarks
                              </th>
                              <th scope="col" className="p-3 text-right">
                                Remaining
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.rows.map((row) => (
                              <tr
                                key={row.row}
                                className="border-t border-borderColor text-primaryText"
                              >
                                <td className="p-3">{row.row}</td>
                                <td className="p-3">
                                  {row.employeeName || row.hrId} ({row.hrId})
                                </td>
                                <td className="p-3">{row.requestId}</td>
                                <td className="p-3">{row.paymentType}</td>
                                <td className="p-3">
                                  {row.financingType === "ESOP_FINANCING"
                                    ? "ESOP financing"
                                    : "Self"}
                                </td>
                                <td className="p-3 text-right tabular-nums">
                                  {formatAmount(row.amount)}
                                </td>
                                <td className="p-3">{row.paymentDate}</td>
                                <td className="p-3">{row.referenceId}</td>
                                <td className="p-3">
                                  {row.financierName
                                    ? `${row.financierName} (${row.financierId})`
                                    : row.modeOfPayment}
                                </td>
                                <td className="p-3">{row.remarks}</td>
                                <td className="p-3 text-right tabular-nums">
                                  {formatAmount(row.remainingAfter)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </VStack>
                  </section>
                )}
              </VStack>
            )}
          </VStack>
          <HStack className="items-center justify-between gap-3 border-t border-borderColor px-6 py-4">
            {step === "PREVIEW" ? (
              <TertiaryCTAButton
                onClick={() => {
                  setStep("UPLOAD");
                }}
                disabled={busy}
              >
                Back
              </TertiaryCTAButton>
            ) : (
              <TertiaryCTAButton onClick={requestClose} disabled={busy}>
                Cancel
              </TertiaryCTAButton>
            )}
            {step === "UPLOAD" && (
              <HStack className="shrink-0">
                <PrimaryCTAButton
                  onClick={previewFile}
                  disabled={!file || busy}
                >
                  {isPreviewing ? "Checking workbook..." : "Preview payments"}
                </PrimaryCTAButton>
              </HStack>
            )}
            {step === "PREVIEW" && preview && (
              <HStack className="shrink-0">
                <PrimaryCTAButton
                  onClick={uploadFile}
                  disabled={busy || preview.errors.length > 0}
                >
                  {isUploading ? "Uploading payments..." : "Upload payments"}
                </PrimaryCTAButton>
              </HStack>
            )}
          </HStack>
        </VStack>
      </Dialog>
      <Dialog
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        aria-labelledby="bulk-payment-discard-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="bulk-payment-discard-title">
          Discard workbook?
        </DialogTitle>
        <DialogContent>
          <p className="text-sm leading-5 text-primaryText">
            Your selected workbook and preview will be cleared.
          </p>
        </DialogContent>
        <DialogActions className="!px-6 !py-4">
          <HStack className="w-full items-center justify-end gap-3">
            <TertiaryCTAButton onClick={() => setConfirmDiscard(false)}>
              Keep editing
            </TertiaryCTAButton>
            <SecondaryCTAButton
              className="!border-red-700 !text-red-700 hover:!bg-red-50"
              onClick={onClose}
            >
              Discard workbook
            </SecondaryCTAButton>
          </HStack>
        </DialogActions>
      </Dialog>
    </>
  );
}
